import { path } from 'ramda'

import { APPOINTMENT_TYPE, ENDPOINTS, LOGIN_TYPES } from '@/lib/consts.ts'
import qs from 'qs'
import { convertReservationToAppointment } from '@/components/appointment/functions.js'
import { createApiClient, transformConfig, useApiClient } from '@/api/client.js'
import { useHCaptcha, useLogger } from '@booksy-com/vue-tools'
import {
  useDatadogLoginActions,
  DATADOG_LOGIN_METHODS,
  DATADOG_LOGIN_ENDPOINTS,
} from '@/components/onboarding/composables/datadog-login-actions/index.js'
import type { Module } from 'vuex'

import type { ApiState, RootState } from '@/types/store'

const logger = useLogger('store.api')
const datadogLoginActions = useDatadogLoginActions()

const handleLoginError = (error) => {
  datadogLoginActions.reportLoginAction({
    isWorkingProperly: error?.response?.status === 400,
    response: error?.response,
    info: DATADOG_LOGIN_ENDPOINTS.LOGIN,
  })

  throw error
}

export const SECURED_ENDPOINTS = [
  {
    url: ENDPOINTS.ACCOUNT_EXISTS,
    key: 'business_account_exists',
  },
  {
    url: ENDPOINTS.ACCOUNT_LOGIN,
    key: 'business_login',
  },
  {
    url: ENDPOINTS.RESET_PASSWORD,
    key: 'business_password_reset',
  },
]

export const H_CAPTCHA_HEADER_NAME = 'X-HCaptcha-Token'

const apiModule = {
  namespaced: true,

  /*!
   *
   * state
   *
   */
  state: {
    apiUrl: '',
    apiKey: '',
    apiVersion: 2,
    publicApiUrl: '',
  },

  /*!
   *
   * actions
   *
   */
  actions: {

    setup: async ({ commit }, data) => {
      await commit('setup', data)
      createApiClient()
    },

    call: async ({ state, rootState, rootGetters, dispatch }, config) => {
      const apiClient = useApiClient()
      return apiClient.call(transformConfig(config))
    },

    callLoyalty: ({ state, rootState, rootGetters }, config) => {
      const apiClient = useApiClient()
      return apiClient.call(transformConfig(config))
    },

    callRoute: ({ state, rootGetters }, config) => {
      const apiClient = useApiClient()
      return apiClient.call(transformConfig(config))
    },

    callHintAndWalkthrough: ({ state, rootState, rootGetters }, config) => {
      const apiClient = useApiClient()
      return apiClient.call(transformConfig(config))
    },

    addHCaptcha: async ({ rootState }, requestConfig) => {
      // skip request object altering when kill-switch is off
      if (!rootState.routingConfig?.config?.hcaptcha_business_frontdesk_enabled) {
        return requestConfig
      }

      const hCaptchaKey = rootState.routingConfig?.config?.hcaptcha_site_keys?.web_biz || null
      const securedUrls = rootState?.routingConfig?.config?.hcaptcha

      // no routingConfig loaded yet
      if (!hCaptchaKey) {
        return requestConfig
      }

      if (!securedUrls) {
        return requestConfig
      }

      const requestPath = requestConfig.url.replace(/\/?(\?.*)?$/, '')
      const method = (requestConfig.method || 'GET').toUpperCase()

      if (securedUrls.find(({ path, methods }) =>
        requestPath.endsWith(path) && Array.isArray(methods) && methods.includes(method)
      )) {
        const hCaptcha = useHCaptcha()

        let token
        try {
          token = await hCaptcha.getToken()
        } catch (e) {
          logger.error('hCaptcha error: ', e)
        }

        if (!token) {
          try {
            token = await hCaptcha.getToken()
          } catch (e) {
            logger.error('hCaptcha retry error: ', e)
          }
        }

        if (token) {
          requestConfig.headers = requestConfig.headers || {}
          requestConfig.headers[H_CAPTCHA_HEADER_NAME] = token
        }
      }
      return requestConfig
    },

    callPublicApi: ({ state, rootState }, config) => {
      const apiClient = useApiClient()
      return apiClient.call(transformConfig(config))
    },

    /*
     *
     * custom api requests...
     *
     */
    login ({ dispatch, rootState }, { email, password }) {
      datadogLoginActions.startLoginProcess(DATADOG_LOGIN_METHODS.EMAIL)
      return dispatch('call', {
        method: 'post',
        url: ENDPOINTS.ACCOUNT_LOGIN,
        data: { email, password },
      }).then(({ data, headers }) => {
        const auth_origin = headers['x-sessionauthorigin']
        return {
          accessToken: data.access_token,
          loginType: LOGIN_TYPES.DEFAULT,
          user: Object.assign({},
            data.account,
            data.access_rights || {},
            { auth_origin }
          ),
        }
      }).catch(handleLoginError)
    },

    loginToken ({ dispatch, rootState }, { accessToken, authToken }) {
      datadogLoginActions.startLoginProcess(DATADOG_LOGIN_METHODS.TOKEN)
      if (authToken) {
        return dispatch('call', {
          method: 'POST',
          url: ENDPOINTS.AUTH_TOKEN,
          data: {
            auth_token: authToken,
          },
        }).then(({ data, headers }) => {
          const auth_origin = headers['x-sessionauthorigin']
          return {
            accessToken: data.access_token,
            loginType: LOGIN_TYPES.AUTH_TOKEN,
            user: Object.assign({},
              data.account,
              data.access_rights || {},
              { auth_origin }
            ),
          }
        })
      } else {
        return dispatch('call', {
          method: 'GET',
          url: ENDPOINTS.ACCOUNT_ME,
          headers: {
            'X-Access-Token': accessToken,
          },
        }).then(({ data, headers }) => {
          const auth_origin = headers['x-sessionauthorigin']
          return {
            accessToken: data.access_token,
            loginType: LOGIN_TYPES.ACCESS_TOKEN,
            user: Object.assign({},
              data.account,
              data.access_rights || {},
              { auth_origin }
            ),
          }
        })
      }
    },

    loginFacebook ({ dispatch, rootState }, { accessToken }) {
      datadogLoginActions.startLoginProcess(DATADOG_LOGIN_METHODS.FACEBOOK)
      return dispatch('call', {
        method: 'POST',
        url: ENDPOINTS.ACCOUNT_LOGIN_FB,
        data: {
          fb_access_token: accessToken,
          auto_register: true,
        },
      }).then(({ data, headers }) => {
        const auth_origin = headers['x-sessionauthorigin']
        return {
          accessToken: data.access_token,
          loginType: LOGIN_TYPES.FACEBOOK,
          user: Object.assign({},
            data.account,
            data.access_rights || {},
            { auth_origin }
          ),
        }
      }).catch(handleLoginError)
    },

    loginGoogle ({ dispatch }, { accessToken }) {
      datadogLoginActions.startLoginProcess(DATADOG_LOGIN_METHODS.GOOGLE)
      return dispatch('call', {
        method: 'POST',
        url: ENDPOINTS.ACCOUNT_LOGIN_GOOGLE,
        data: {
          id_token: accessToken,
          auto_register: true,
        },
      }).then(({ data, headers }) => {
        const auth_origin = headers['x-sessionauthorigin']
        return {
          accessToken: data.access_token,
          loginType: LOGIN_TYPES.GOOGLE,
          user: Object.assign({},
            data.account,
            data.access_rights || {},
            { auth_origin }
          ),
        }
      }).catch(handleLoginError)
    },

    loginApple ({ dispatch, rootState }, data) {
      datadogLoginActions.startLoginProcess(DATADOG_LOGIN_METHODS.APPLE)
      return dispatch('call', {
        method: 'POST',
        url: ENDPOINTS.ACCOUNT_LOGIN_APPLE,
        data,
      }).then(({ data, headers }) => {
        const auth_origin = headers['x-sessionauthorigin']
        return {
          accessToken: data.access_token,
          loginType: LOGIN_TYPES.APPLE,
          user: Object.assign({},
            data.account,
            data.access_rights || {},
            { auth_origin }
          ),
        }
      }).catch(handleLoginError)
    },

    connectFacebook ({ dispatch }, { accessToken }) {
      return dispatch('call', {
        method: 'POST',
        url: ENDPOINTS.CONNECT_WITH_FACEBOOK,
        data: {
          fb_access_token: accessToken,
        },
      })
    },

    connectGoogle ({ dispatch }, { accessToken }) {
      return dispatch('call', {
        method: 'POST',
        url: ENDPOINTS.CONNECT_WITH_GOOGLE,
        data: {
          id_token: accessToken,
        },
      })
    },

    logout ({ dispatch }) {
      return dispatch('call', {
        method: 'POST',
        url: ENDPOINTS.ACCOUNT_LOGOUT,
      }).catch((error) => {
        // shortcut: user is already logged out
        if (error.response.status === 403) {
          return true
        }
        throw error
      })
    },

    getCustomers: ({ dispatch, rootState }, { params }) => {
      const cfg = {
        method: 'GET',
        url: ENDPOINTS.CUSTOMERS,
        urlParams: {
          businessId: rootState.business.business.id,
        },
        params,
        paramsSerializer: params => qs.stringify(params, { indices: false }),
      }

      return dispatch('call', cfg)
    },

    getCustomerById: ({ dispatch, rootState }, { urlParams, params = null }) => {
      const cfg = {
        method: 'GET',
        url: ENDPOINTS.BUSINESS_CUSTOMER,
        urlParams: {
          businessId: rootState.business.business.id,
          ...urlParams,
        },
        params,
      }

      return dispatch('call', cfg)
    },

    /*
     *
     * load appointment
     *
     */
    appointmentLoad ({ dispatch, rootState }, { id, type }) {
      const businessId = rootState.business.business.id

      const config: {
        url: string
        urlParams: Record<string, unknown>
      } = {
        url: ENDPOINTS.APPOINTMENT_UID,
        urlParams: {
          businessId,
          appointmentUid: id,
        },
      }

      if (type && [APPOINTMENT_TYPE.SINGLE, APPOINTMENT_TYPE.MULTI].includes(type)) {
        config.url = ENDPOINTS.APPOINTMENT
        config.urlParams = {
          businessId,
          appointmentType: type,
          appointmentId: id,
        }
      }

      return dispatch(
        'call',
        config
      ).then((response) => {
        const appointment = response.data.appointment
        const customer = response.data.customer
        const physioVisitRedirect = response.data.physio_visit_redirect
        return {
          appointment,
          customer,
          physioVisitRedirect,
        }
      })
    },

    /*
     *
     * save appointment
     * see: @/store/modules/appointment
     * @param {Object} appointment
     * @param {Boolean} dry run
     * @param {Boolean} force overbooking
     * @param {Boolean} notify customer about reschedule
     * @param {Boolean} update future bookings
     * @param {Boolean} preserve sub-bookings order
     * @param {Boolean} enable notifications
     *
     */
    appointmentSave ({ dispatch, rootState }, {
      appointment,
      dryRun = false,
      overbooking = false,
      notifyAboutReschedule = false,
      updateFutureBookings = undefined,
      preserveOrder = false,
      notificationEnabled = true,
      headers,
    }) {
      // current business identifier
      const businessId = rootState.business.business.id

      // is update? ...or create?
      const isUpdate = !!appointment.appointment_id

      // request method
      const method = (isUpdate) ? 'put' : 'post'

      // request url
      let url = isUpdate
        ? ENDPOINTS.APPOINTMENT_UID
        : ENDPOINTS.APPOINTMENTS

      if (dryRun) {
        url = isUpdate
          ? ENDPOINTS.APPOINTMENT_UID_DRYRUN
          : ENDPOINTS.APPOINTMENTS_DRYRUN
      }

      // request url parameters
      const urlParams: Record<string, unknown> = { businessId }

      if (isUpdate) {
        urlParams.appointmentUid = appointment.appointment_uid
      }

      // request data
      const data = {
        ...appointment,
        overbooking: overbooking,
        _notification_enabled: notificationEnabled,
        _notify_about_reschedule: notifyAboutReschedule,
        _update_future_bookings: updateFutureBookings,
        _preserve_order: preserveOrder,
      }

      // make request
      return dispatch('call', {
        url,
        urlParams,
        method,
        data,
        ...(headers && { headers }),
      })
    },

    /*
     *
     * get reservation
     *
     */
    reservation ({ dispatch }, { id }) {
      return dispatch(
        'call',
        {
          url: ENDPOINTS.RESERVATION,
          urlParams: {
            reservationId: id,
          },
        }
      ).then(({ data: { reservation } }) => {
        const appointment = convertReservationToAppointment(reservation)

        return {
          appointment,
        }
      })
    },

    /*
     *
     * delete reservation
     *
     */
    reservationDelete ({ dispatch }, { id }) {
      return dispatch(
        'call',
        {
          url: ENDPOINTS.RESERVATION,
          method: 'delete',
          urlParams: {
            reservationId: id,
          },
        }
      ).then(({ data }) => {
        return data
      })
    },

    /*
     *
     * save reservation
     * @param {Object} reservation
     *
     */
    reservationSave ({ dispatch, rootState }, {
      reservation,
      overbooking = false,
    }) {
      const businessId = rootState.business.business.id

      // is update? ...or create?
      const isUpdate = !!reservation.id

      // request method
      const method = (isUpdate) ? 'put' : 'post'

      const urlParams: Record<string, unknown> = {}
      if (isUpdate) {
        urlParams.reservationId = reservation.id
      } else {
        urlParams.businessId = businessId
      }

      // request data
      const data = {
        ...reservation,
        overbooking,
      }

      return dispatch(
        'call',
        {
          url: isUpdate ? ENDPOINTS.RESERVATION : ENDPOINTS.RESERVATION_NEW,
          method,
          urlParams,
          data,
        }
      )
    },

    /*
     *
     * load reservation
     *
     */

    transactionAction: ({ dispatch, rootState }, { transactionId, paymentToken, action, payment_status, row_id, headers }) => {
      const cfg = {
        method: 'POST',
        url: ENDPOINTS.POS_TRANSACTION_ACTION,
        urlParams: {
          businessId: rootState.business.business.id,
          transactionId,
        },
        data: {
          action,
          payment_token: paymentToken,
          selected_register_id: path(['id'], rootState.cashRegister.selectedRegister),
          payment_status,
          row_id,
        },
        ...(headers && { headers }),
      }

      return dispatch('call', cfg)
    },

    /*
     *
     * load mapkit token
     *
     */

    getMapkitToken ({ dispatch, rootState }) {
      const params: Record<string, unknown> = {}

      if (rootState.app.mode === 'production') {
        params.domain = rootState.app.baseUrl
      }

      return dispatch('call', {
        url: ENDPOINTS.MAPKIT_TOKEN,
        params,
      })
    },
  },

  /*!
   *
   * mutations
   *
   */
  mutations: {

    setup: (state, { apiUrl, apiKey, publicApiUrl }) => {
      state.apiUrl = apiUrl
      state.apiKey = apiKey
      state.publicApiUrl = publicApiUrl
    },
  },
} as Module<ApiState, RootState>

export default apiModule
